import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  HelpCircle, Plus, Pencil, Trash2, Loader2, Save, FolderOpen,
} from "lucide-react";

export default function AdminFAQ() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCat, setEditCat] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [catRes, itemRes] = await Promise.all([
        api.get("/admin/faq/categories"),
        api.get("/admin/faq/items"),
      ]);
      setCategories(catRes.data);
      setItems(itemRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // ----------- Category CRUD -----------
  const saveCat = async () => {
    setSaving(true);
    try {
      if (editCat.id) {
        await api.put(`/admin/faq/categories/${editCat.id}`, editCat);
      } else {
        await api.post("/admin/faq/categories", editCat);
      }
      setEditCat(null);
      fetchAll();
      toast.success("Category saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteCat = async (id) => {
    if (!window.confirm("Delete this category and all its FAQs?")) return;
    await api.delete(`/admin/faq/categories/${id}`);
    fetchAll();
    toast.success("Category deleted");
  };

  // ----------- Item CRUD -----------
  const saveItem = async () => {
    setSaving(true);
    try {
      if (editItem.id) {
        await api.put(`/admin/faq/items/${editItem.id}`, editItem);
      } else {
        await api.post("/admin/faq/items", editItem);
      }
      setEditItem(null);
      fetchAll();
      toast.success("FAQ saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    await api.delete(`/admin/faq/items/${id}`);
    fetchAll();
    toast.success("FAQ deleted");
  };

  const catName = (catId) => categories.find((c) => c.id === catId)?.name || "Uncategorized";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-faq-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl flex items-center gap-2">
            <HelpCircle className="w-7 h-7 text-primary" />
            FAQ Manager
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Manage FAQ categories and questions</p>
        </div>
      </div>

      {/* Categories Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-lg flex items-center gap-2">
            <FolderOpen className="w-5 h-5" /> Categories
          </h2>
          <Button
            size="sm"
            onClick={() => setEditCat({ name: "", icon: "HelpCircle", display_order: categories.length, is_active: true })}
            data-testid="add-faq-category"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Category
          </Button>
        </div>
        {loading ? (
          <div className="h-16 bg-muted animate-pulse rounded-lg" />
        ) : (
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <div key={cat.id} className="flex items-center gap-2 border border-border rounded-lg px-3 py-2 bg-card">
                <span className="text-sm font-medium">{cat.name}</span>
                <Badge variant={cat.is_active ? "default" : "secondary"} className="text-[10px]">
                  {cat.is_active ? "Active" : "Inactive"}
                </Badge>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditCat({ ...cat })} data-testid={`edit-faq-cat-${cat.id}`}>
                  <Pencil className="w-3 h-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => deleteCat(cat.id)} data-testid={`delete-faq-cat-${cat.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FAQ Items */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-lg">FAQ Items ({items.length})</h2>
          <Button
            size="sm"
            onClick={() => setEditItem({
              question: "", answer: "", category_id: categories[0]?.id || "",
              tags: [], display_order: items.length, is_featured: false, is_active: true,
            })}
            data-testid="add-faq-item"
          >
            <Plus className="w-4 h-4 mr-1" /> Add FAQ
          </Button>
        </div>
        {loading ? (
          <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}</div>
        ) : items.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">No FAQ items yet</Card>
        ) : (
          <div className="space-y-2" data-testid="faq-items-list">
            {items.map((it) => (
              <Card key={it.id} className="p-4" data-testid={`faq-item-row-${it.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{it.question}</span>
                      {it.is_featured && <Badge variant="outline" className="text-[10px]">Featured</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">{it.answer}</p>
                    <span className="text-[10px] text-muted-foreground mt-1 block">{catName(it.category_id)}</span>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditItem({ ...it })} data-testid={`edit-faq-item-${it.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteItem(it.id)} data-testid={`delete-faq-item-${it.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Category Sheet */}
      <Sheet open={!!editCat} onOpenChange={(v) => !v && setEditCat(null)}>
        <SheetContent data-testid="faq-cat-sheet">
          <SheetHeader>
            <SheetTitle>{editCat?.id ? "Edit Category" : "New Category"}</SheetTitle>
          </SheetHeader>
          {editCat && (
            <div className="mt-4 space-y-4">
              <div>
                <Label>Name</Label>
                <Input value={editCat.name} onChange={(e) => setEditCat({ ...editCat, name: e.target.value })} data-testid="faq-cat-name" />
              </div>
              <div>
                <Label>Icon (Lucide name)</Label>
                <Input value={editCat.icon} onChange={(e) => setEditCat({ ...editCat, icon: e.target.value })} data-testid="faq-cat-icon" />
              </div>
              <div>
                <Label>Display Order</Label>
                <Input type="number" value={editCat.display_order} onChange={(e) => setEditCat({ ...editCat, display_order: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editCat.is_active} onCheckedChange={(v) => setEditCat({ ...editCat, is_active: v })} />
                <Label>Active</Label>
              </div>
              <Button onClick={saveCat} disabled={saving || !editCat.name} data-testid="faq-cat-save">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save Category
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Edit Item Sheet */}
      <Sheet open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)}>
        <SheetContent className="sm:w-[500px] overflow-y-auto" data-testid="faq-item-sheet">
          <SheetHeader>
            <SheetTitle>{editItem?.id ? "Edit FAQ" : "New FAQ"}</SheetTitle>
          </SheetHeader>
          {editItem && (
            <div className="mt-4 space-y-4">
              <div>
                <Label>Category</Label>
                <Select value={editItem.category_id} onValueChange={(v) => setEditItem({ ...editItem, category_id: v })}>
                  <SelectTrigger data-testid="faq-item-cat-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Question</Label>
                <Input value={editItem.question} onChange={(e) => setEditItem({ ...editItem, question: e.target.value })} data-testid="faq-item-question" />
              </div>
              <div>
                <Label>Answer</Label>
                <Textarea
                  value={editItem.answer}
                  onChange={(e) => setEditItem({ ...editItem, answer: e.target.value })}
                  rows={5}
                  data-testid="faq-item-answer"
                />
              </div>
              <div>
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={(editItem.tags || []).join(", ")}
                  onChange={(e) => setEditItem({ ...editItem, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  data-testid="faq-item-tags"
                />
              </div>
              <div>
                <Label>Display Order</Label>
                <Input type="number" value={editItem.display_order} onChange={(e) => setEditItem({ ...editItem, display_order: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch checked={editItem.is_featured} onCheckedChange={(v) => setEditItem({ ...editItem, is_featured: v })} />
                  <Label>Featured</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editItem.is_active} onCheckedChange={(v) => setEditItem({ ...editItem, is_active: v })} />
                  <Label>Active</Label>
                </div>
              </div>
              <Button onClick={saveItem} disabled={saving || !editItem.question || !editItem.answer} className="w-full" data-testid="faq-item-save">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save FAQ
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
